import { useMemo } from "react";
import { useUser } from "@clerk/clerk-react";
import { useQuery } from "@tanstack/react-query";
import { useApiAuth } from "./useApiAuth";
import { apiUrl } from "../lib/api";
import { queryKeys } from "../lib/queryKeys";

export type LeaderboardEntry = {
  rank: number | null;
  trader_code: string | null;
  display_name?: string | null;
  avatar_url?: string | null;
  points_balance: number;
  unrealized_pnl: number;
  realized_pnl: number;
  total_pnl: number;
  settled_value: number;
  total_value: number;
  trade_count: number;
};

export type VirtualTraderDisclosure = {
  trader_code?: string | null;
  display_name: string;
  participant_type: "VIRTUAL";
  profile: string;
  profile_description: string;
  status: string;
  pause_reason?: string | null;
};

export type LeaderboardResponse = {
  pool_code: string;
  pool_name: string;
  point_monetary_value: number | null;
  competition_code: string;
  season_code: string;
  refreshed_at?: string | null;
  refresh_source?: "live" | "snapshot";
  leaderboard: LeaderboardEntry[];
  virtual_traders?: VirtualTraderDisclosure[];
};

type UseLeaderboardArgs = {
  poolCode: string | null;
  enabled?: boolean;
};

const buildUrl = (poolCode: string) => apiUrl(`/api/leaderboard?poolCode=${encodeURIComponent(poolCode)}`);

export function useLeaderboard({ poolCode, enabled }: UseLeaderboardArgs) {
  const isFeatureEnabled = enabled ?? true;
  const { acquireToken } = useApiAuth();
  const { isSignedIn: isAuthenticated } = useUser();
  const canLoad = Boolean(isFeatureEnabled && isAuthenticated && poolCode);
  const queryKey = useMemo(() => queryKeys.leaderboard(poolCode ?? "none"), [poolCode]);

  const query = useQuery({
    queryKey,
    enabled: canLoad,
    queryFn: async () => {
      if (!poolCode) return null;

      const accessToken = await acquireToken();
      if (!accessToken) {
        throw new Error("Authentication required.");
      }

      const response = await fetch(buildUrl(poolCode), {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load leaderboard (${response.status})`);
      }

      const payload: LeaderboardResponse = await response.json();
      return payload;
    },
  });

  return {
    data: canLoad ? query.data ?? null : null,
    isLoading: canLoad ? query.isLoading : false,
    isError: canLoad ? query.isError : false,
    reload: async () => {
      await query.refetch();
    },
  };
}

export type UseLeaderboardResult = ReturnType<typeof useLeaderboard>;
